import type { TimelinePolicy } from '@/repositories/InsurancePolicyTimelineRepository';
import type { PillProps } from '@/ui';
import usFormatDateString from '@/utils/usFormatDateString';

import {
  SENTINEL_POLICY_LABELS,
  cleanPolicyValue,
  formatCoverageRange,
  formatPlanDate,
  formatPolicyLabel
} from '../../utils';

import type { PolicyBannerFact, PolicyIdentifier, PolicyRailColor, PolicyStatus } from './types';

const POLICY_RAIL_COLORS: PolicyRailColor[] = [
  {
    border: 'border-l-blue-400',
    chip: 'bg-blue-100 text-blue-900',
    section: 'border border-blue-200 bg-blue-50'
  },
  {
    border: 'border-l-violet-400',
    chip: 'bg-violet-100 text-violet-900',
    section: 'border border-violet-200 bg-violet-50'
  },
  {
    border: 'border-l-emerald-400',
    chip: 'bg-emerald-100 text-emerald-900',
    section: 'border border-emerald-200 bg-emerald-50'
  },
  {
    border: 'border-l-amber-400',
    chip: 'bg-amber-100 text-amber-900',
    section: 'border border-amber-200 bg-amber-50'
  },
  {
    border: 'border-l-cyan-400',
    chip: 'bg-cyan-100 text-cyan-900',
    section: 'border border-cyan-200 bg-cyan-50'
  }
];

const SENTINEL_RAIL_COLOR: PolicyRailColor = {
  border: 'border-l-gray-300',
  chip: 'bg-gray-100 text-gray-700',
  section: 'border border-gray-200 bg-gray-50'
};

export const POLICY_STATUS_COLORS: Record<PolicyStatus, PillProps['color']> = {
  Active: 'primary',
  Ended: 'neutral',
  Future: 'secondary',
  Deleted: 'destructive'
};

// Colours are claimed by the policies most likely to be compared.  Indexing on
// roster position alone let two live policies collide whenever they sat a full
// palette apart, which is exactly the pair a reader is trying to tell apart.
const STATUS_COLOR_PRIORITY: Record<PolicyStatus, number> = {
  Active: 0,
  Future: 1,
  Ended: 2,
  Deleted: 3
};

function colorOrder(roster: TimelinePolicy[]): string[] {
  return roster
    .map((policy, index) => ({ policy, index }))
    .sort(
      (a, b) =>
        STATUS_COLOR_PRIORITY[derivePolicyStatus(a.policy)] -
          STATUS_COLOR_PRIORITY[derivePolicyStatus(b.policy)] || a.index - b.index
    )
    .map(({ policy }) => policy.id);
}

export function policyRailColor(policyId: string, roster: TimelinePolicy[]): PolicyRailColor {
  const index = colorOrder(roster).indexOf(policyId);
  if (index < 0) return SENTINEL_RAIL_COLOR;
  return POLICY_RAIL_COLORS[index % POLICY_RAIL_COLORS.length];
}

export function policyRailLabel(policyId: string, roster: TimelinePolicy[]): string {
  const policy = roster.find((entry) => entry.id === policyId);
  if (policy) return formatPolicyLabel(policy, roster);
  return SENTINEL_POLICY_LABELS[policyId] || 'Unknown policy';
}

export function derivePolicyStatus(policy: TimelinePolicy): PolicyStatus {
  if (policy.deleted_at) return 'Deleted';

  const today = new Date().toISOString().split('T')[0];
  if (policy.plan_end_date && policy.plan_end_date < today) return 'Ended';
  if (policy.plan_start_date && policy.plan_start_date > today) return 'Future';

  return 'Active';
}

export function policyStatusTitle(policy: TimelinePolicy): string {
  const status = derivePolicyStatus(policy);

  if (status === 'Deleted') {
    return policy.deleted_at ? `Deleted ${usFormatDateString(policy.deleted_at)}` : 'Deleted';
  }
  if (status === 'Ended' && policy.plan_end_date) {
    return `Coverage ended ${formatPlanDate(policy.plan_end_date)}`;
  }
  if (status === 'Future' && policy.plan_start_date) {
    return `Coverage starts ${formatPlanDate(policy.plan_start_date)}`;
  }

  return 'Coverage active';
}

// Shown on every banner, not only when two policies would otherwise read alike —
// a lone policy still needs to say what it currently is.  Names and identifiers live
// in tooltips instead, so the row stays on one line.
export function policyBannerFacts(policy: TimelinePolicy): PolicyBannerFact[] {
  const facts: PolicyBannerFact[] = [];

  const priority = cleanPolicyValue(policy.priority_type);
  if (priority) facts.push({ text: priority, title: `Coordination of benefits: ${priority}` });

  const coverage = formatCoverageRange(policy);
  if (coverage) facts.push({ text: coverage, title: `Coverage: ${coverage}` });

  return facts;
}

export function policyNameDetails(policy: TimelinePolicy): PolicyIdentifier[] {
  const provider = cleanPolicyValue(policy.insurance_provider_name);
  const carrier = cleanPolicyValue(policy.carrier);

  return [
    { label: 'Carrier', value: carrier || provider },
    { label: 'Provider', value: provider && provider !== carrier ? provider : null },
    { label: 'Plan', value: cleanPolicyValue(policy.plan) },
    { label: 'Type', value: cleanPolicyValue(policy.type) }
  ].filter((detail) => detail.value) as PolicyIdentifier[];
}

export function policyIdentifiers(policy: TimelinePolicy): PolicyIdentifier[] {
  return [
    { label: 'Policy ID', value: policy.id },
    { label: 'Member ID', value: cleanPolicyValue(policy.policy_member_id) },
    { label: 'Subscriber ID', value: cleanPolicyValue(policy.subscriber_id) },
    { label: 'Group ID', value: cleanPolicyValue(policy.group_id) },
    { label: 'Healthie ID', value: policy.healthie_id ? String(policy.healthie_id) : null }
  ].filter((identifier) => identifier.value) as PolicyIdentifier[];
}
